import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Input, Button, LoadingSpinner, ConfirmDialog } from '../../../../components';
import { AgentCapabilityPicker } from '../../../../components/AgentCapabilityChips';
import { AgentChainPicker } from '../../../../components/AgentChainChips';
import { labelForChain } from '../../../../components/AgentChainChips/agentChainLabels';
import {
  formatEndpointPrice,
  parseAmountToSmallest,
  smallestToWhole,
  PAYMENT_ASSETS,
  PAYMENT_ASSET_ALLOWED_CHAINS,
  ALL_PAYMENT_CHAINS,
} from '../../../../utils/formatEndpointPrice';
import { useAppDispatch, useAppSelector } from '../../../../redux/Store';
import api from '../../../../utils/api';
import {
  fetchOwnedAgentDetail,
  updateOwnedAgent,
  updateOwnedAgentEndpoint,
  deleteOwnedAgentEndpoint,
  setPrimaryOwnedAgentWallet,
  deleteOwnedAgentWallet,
  addOwnedAgentAcceptedSettlement,
  deleteOwnedAgentAcceptedSettlement,
  AgentEndpointMethod,
} from '../../../../redux/thunks/agentThunks';
import { resetAgentEdit } from '../../../../redux/slices/agentSlice';
import {
  selectAgentEditDetail,
  selectAgentEditLoadStatus,
  selectAgentEditLoadError,
  selectAgentEditSaveStatus,
  selectAgentEditSaveRejection,
  selectWalletSaveStatus,
} from '../../../../redux/selectors/agentSelectors';
import { AddEndpointModal } from './modals/AddEndpointModal';
import { AddWalletModal } from './modals/AddWalletModal';
import { VerifyEndpointModal } from './modals/VerifyEndpointModal';
import { VerifyEmailModal } from './modals/VerifyEmailModal';
import { VerifyDnsModal } from './modals/VerifyDnsModal';
import '../MyAgents.scss';
import './EditAgent.scss';

const METHODS: AgentEndpointMethod[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

// Wallet addresses are too long to display in full on cards / rows —
// especially on mobile — so show first-6 + last-6 with an ellipsis
// in the middle. Anything ≤12 chars is left as-is (no gain from
// abbreviating). Callers should pass the FULL address as a `title`
// attribute so hover reveals it.
function shortAddress(addr: string): string {
  if (!addr || addr.length <= 12) return addr;
  return `${addr.slice(0, 6)}…${addr.slice(-6)}`;
}

/**
 * Edit Agent — ADR-011 Ring 2.5.
 *
 * Owner-scoped edit form for a single agent. Left column: mutable
 * profile fields (displayName, bio, capabilities). Right column:
 * endpoint editor (add / delete). Everything server-driven — client
 * only holds transient form state; on save we PATCH and let the
 * response replace the source-of-truth in Redux.
 *
 * Kept intentionally simple for MVP: no inline endpoint editing
 * (edit = delete + add), no avatar upload here (avatar is set at
 * registration; can be a follow-up).
 */
const EditAgent: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { agentUserId } = useParams<{ agentUserId: string }>();

  const detail = useAppSelector(selectAgentEditDetail);
  const loadStatus = useAppSelector(selectAgentEditLoadStatus);
  const loadError = useAppSelector(selectAgentEditLoadError);
  const saveStatus = useAppSelector(selectAgentEditSaveStatus);
  const saveRejection = useAppSelector(selectAgentEditSaveRejection);
  // Wallet-save status still needed for disabling promote/delete
  // buttons on rows while an add/promote/delete is in flight.
  // Endpoint-save status + both rejection slots moved with their
  // consumers into the modal components.
  const walletSaveStatus = useAppSelector(selectWalletSaveStatus);

  // Form state (initialized from detail once it loads)
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [capabilities, setCapabilities] = useState<string[]>([]);
  const [chains, setChains] = useState<string[]>([]);

  // Add-endpoint + add-wallet composers now live in dedicated
  // modals (AddEndpointModal / AddWalletModal). Page just holds
  // isOpen state for each; the modals own their own form state
  // + submit handlers + Redux dispatches.
  const [addEndpointOpen, setAddEndpointOpen] = useState(false);
  const [addWalletOpen, setAddWalletOpen] = useState(false);

  // Inline endpoint editor — one row editable at a time. editingId
  // is the endpoint currently in edit mode (null = none). Drafts
  // hold the working values; on Save we PATCH + on Cancel we drop
  // them. editError shows a duplicate-URL rejection inline on the
  // row so it reads next to the field that caused it.
  const [editingEndpointId, setEditingEndpointId] = useState<string | null>(null);
  const [editDraftLabel, setEditDraftLabel] = useState('');
  const [editDraftUrl, setEditDraftUrl] = useState('');
  const [editDraftDescription, setEditDraftDescription] = useState('');
  const [editDraftMethod, setEditDraftMethod] = useState<AgentEndpointMethod>('POST');
  // Draft pricing state for inline edit. Whole-unit strings (matching
  // composer), asset from the loaded row. Populated when the row
  // enters edit mode; cleared on cancel/save.
  const [editDraftPriceMin, setEditDraftPriceMin] = useState('');
  const [editDraftPriceMax, setEditDraftPriceMax] = useState('');
  const [editDraftPriceAsset, setEditDraftPriceAsset] = useState('USDC');
  const [editDraftError, setEditDraftError] = useState<string | null>(null);
  const [isSavingEndpoint, setIsSavingEndpoint] = useState(false);

  // Pending-delete endpoint — stores the row the user is confirming
  // delete on. null = modal closed. We stash the whole row (not just
  // the id) so the modal message can name the specific endpoint by
  // its method + URL rather than a generic "this endpoint".
  const [pendingDeleteEndpoint, setPendingDeleteEndpoint] = useState<{
    id: string;
    method: AgentEndpointMethod;
    url: string;
    label: string | null;
  } | null>(null);
  const [isDeletingEndpoint, setIsDeletingEndpoint] = useState(false);

  // Wallet delete-confirm state. (Add-wallet composer moved into
  // AddWalletModal.) Stash the row being deleted so the modal can
  // name it, mirroring the endpoint delete-confirm.
  const [pendingDeleteWallet, setPendingDeleteWallet] = useState<{
    id: string;
    chain: string;
    address: string;
    label: string | null;
  } | null>(null);
  const [isDeletingWallet, setIsDeletingWallet] = useState(false);

  // ADR-012 verifications for this agent. Loaded once via the
  // owner-facing GET route (#PR G1) + refreshed after any modal
  // reports a successful check. Stored raw + narrowed per type at
  // render time so the same source of truth feeds the endpoint
  // status chips, the email list, and the DNS list.
  type VerificationRow = {
    id: string;
    type: 'email' | 'endpoint' | 'dns_txt';
    targetValue: string;
    status: 'pending' | 'verified' | 'stale' | 'revoked';
    verifiedAt: string | null;
    staleSince?: string | null;
  };
  const [verifications, setVerifications] = useState<VerificationRow[]>([]);
  // Endpoint status chips key on the URL — one row per endpoint URL
  // by design; if the owner rotates the URL, the previous row goes
  // stale and drops out of this map on next refetch.
  const endpointVerificationsByUrl: Record<string, VerificationRow> =
    Object.fromEntries(
      verifications
        .filter((v) => v.type === 'endpoint' && v.status !== 'revoked')
        .map((v) => [v.targetValue, v]),
    );
  const emailVerifications = verifications.filter(
    (v) => v.type === 'email' && v.status !== 'revoked',
  );
  const dnsVerifications = verifications.filter(
    (v) => v.type === 'dns_txt' && v.status !== 'revoked',
  );
  const [verifyEndpointId, setVerifyEndpointId] = useState<string | null>(null);
  const [verifyEmailOpen, setVerifyEmailOpen] = useState(false);
  const [verifyDnsOpen, setVerifyDnsOpen] = useState(false);

  // Small helper: refetch verifications from the server. Used after
  // any modal reports success + inside the initial mount effect.
  const refetchVerifications = useCallback(() => {
    if (!agentUserId) return;
    api
      .get<{ verifications: VerificationRow[] }>(
        `/agents/mine/${agentUserId}/verifications`,
      )
      .then((res) => setVerifications(res.data.verifications))
      .catch(() => {
        /* silent — degrades to empty status chips */
      });
  }, [agentUserId]);

  // Live displayName availability check — mirrors the register page,
  // but scoped to "did the user change it?". If the trimmed value
  // still matches the loaded agent's displayName, the check is
  // short-circuited to 'idle' (the endpoint doesn't self-exclude
  // and would false-positive as 'taken' against the agent's own
  // profile row). Any other value hits the same availability
  // endpoint used at registration.
  type DnStatus = 'idle' | 'checking' | 'available' | 'taken';
  const [dnStatus, setDnStatus] = useState<DnStatus>('idle');

  useEffect(() => {
    if (agentUserId) dispatch(fetchOwnedAgentDetail(agentUserId));
    return () => {
      dispatch(resetAgentEdit());
    };
  }, [dispatch, agentUserId]);

  // ADR-012 verifications — hydrate on mount for this agent.
  // Separate fetch (not folded into fetchOwnedAgentDetail) so the
  // verification stack lands additively without touching the
  // AgentService detail shape. Silent on failure — degrades to
  // empty state (no chips, empty lists).
  useEffect(() => {
    refetchVerifications();
  }, [refetchVerifications]);

  // Hydrate form when detail loads
  useEffect(() => {
    if (detail) {
      setDisplayName(detail.agent.displayName ?? '');
      setBio(detail.agent.bio ?? '');
      setCapabilities(detail.agent.capabilities ?? []);
      setChains(detail.agent.chains ?? []);
    }
  }, [detail]);

  // Derived dirty flag — form differs from the loaded source of
  // truth. Drives the "Saved" indicator (hidden once user edits
  // again post-save) + gates the Save button (nothing to save
  // when form matches the last-saved state).
  const isProfileDirty = !!detail && (
    displayName.trim() !== (detail.agent.displayName ?? '') ||
    bio !== (detail.agent.bio ?? '') ||
    JSON.stringify([...capabilities].sort()) !==
      JSON.stringify([...(detail.agent.capabilities ?? [])].sort()) ||
    JSON.stringify([...chains].sort()) !==
      JSON.stringify([...(detail.agent.chains ?? [])].sort())
  );

  // Debounced availability check — 400ms after the user stops typing.
  // Skips network calls when the value is empty OR unchanged from the
  // loaded agent's own displayName (case-sensitive trim compare — the
  // server does LOWER() so we're a bit stricter than the check, but
  // that's fine: worst case is one wasted request that comes back
  // available since /agents/display-name/availability excludes nothing).
  useEffect(() => {
    const trimmed = displayName.trim();
    const original = detail?.agent.displayName ?? '';
    if (trimmed.length === 0 || trimmed === original) {
      setDnStatus('idle');
      return;
    }
    setDnStatus('checking');
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get<{ available: boolean }>(
          '/agents/display-name/availability',
          { params: { value: trimmed } },
        );
        setDnStatus(data.available ? 'available' : 'taken');
      } catch {
        setDnStatus('idle');
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [displayName, detail]);

  const handleSaveProfile = async (e: FormEvent) => {
    e.preventDefault();
    if (!agentUserId) return;
    const patch: {
      displayName?: string;
      bio?: string | null;
      capabilities?: string[];
      chains?: string[];
    } = {};
    const trimmedName = displayName.trim();
    if (trimmedName && trimmedName !== (detail?.agent.displayName ?? '')) {
      patch.displayName = trimmedName;
    }
    // Bio: null when cleared, string when set
    if (bio !== (detail?.agent.bio ?? '')) {
      patch.bio = bio.trim().length > 0 ? bio.trim() : null;
    }
    if (
      JSON.stringify([...capabilities].sort()) !==
      JSON.stringify([...(detail?.agent.capabilities ?? [])].sort())
    ) {
      patch.capabilities = capabilities;
    }
    if (
      JSON.stringify([...chains].sort()) !==
      JSON.stringify([...(detail?.agent.chains ?? [])].sort())
    ) {
      patch.chains = chains;
    }
    if (Object.keys(patch).length === 0) return;
    const result = await dispatch(updateOwnedAgent({ agentUserId, patch }));
    // Toast on success + failure. Field-level rejections (displayName
    // taken) still surface inline via the Input's error prop; toast
    // is the transient positive/negative signal.
    if (result.meta.requestStatus === 'fulfilled') {
      toast.success('Agent updated');
    } else {
      const payload = result.payload as
        | { kind: string; message: string }
        | undefined;
      // display_name_taken is already visible inline; skip the
      // duplicate toast so users don't see the same message twice.
      if (payload && payload.kind !== 'display_name_taken') {
        toast.error(payload.message || 'Failed to update agent');
      }
    }
  };

  // handleAddEndpoint moved to AddEndpointModal.

  const handleStartEditEndpoint = (ep: {
    id: string;
    label: string | null;
    url: string;
    description: string | null;
    method: AgentEndpointMethod;
    priceMinSmallest: string;
    priceMaxSmallest: string;
    priceAsset: string;
  }) => {
    setEditingEndpointId(ep.id);
    setEditDraftLabel(ep.label ?? '');
    setEditDraftUrl(ep.url);
    setEditDraftDescription(ep.description ?? '');
    setEditDraftMethod(ep.method);
    // Hydrate whole-unit strings from smallest so the input reads
    // '0.5' not '500000'. Empty string for 0 so the field looks
    // blank when the endpoint is Free — matches the composer's
    // "blank = Free" affordance.
    setEditDraftPriceMin(
      ep.priceMinSmallest === '0'
        ? ''
        : smallestToWhole(ep.priceMinSmallest, ep.priceAsset),
    );
    setEditDraftPriceMax(
      ep.priceMaxSmallest === '0'
        ? ''
        : smallestToWhole(ep.priceMaxSmallest, ep.priceAsset),
    );
    setEditDraftPriceAsset(ep.priceAsset);
    setEditDraftError(null);
  };

  const handleCancelEditEndpoint = () => {
    setEditingEndpointId(null);
    setEditDraftError(null);
  };

  const handleSaveEditedEndpoint = async () => {
    if (!agentUserId || !editingEndpointId) return;
    if (!editDraftUrl.trim()) {
      setEditDraftError('URL is required.');
      return;
    }
    // Same parse rules as the composer.
    const minSmallest = editDraftPriceMin.trim() === ''
      ? '0'
      : parseAmountToSmallest(editDraftPriceMin, editDraftPriceAsset);
    const maxSmallest = editDraftPriceMax.trim() === ''
      ? '0'
      : parseAmountToSmallest(editDraftPriceMax, editDraftPriceAsset);
    if (minSmallest === null || maxSmallest === null) {
      setEditDraftError(`Enter a valid ${editDraftPriceAsset} amount (e.g. 0.5).`);
      return;
    }
    if (BigInt(maxSmallest) < BigInt(minSmallest)) {
      setEditDraftError('Max price must be at least the min price.');
      return;
    }
    setEditDraftError(null);
    setIsSavingEndpoint(true);
    const result = await dispatch(
      updateOwnedAgentEndpoint({
        agentUserId,
        endpointId: editingEndpointId,
        patch: {
          label: editDraftLabel.trim() || null,
          url: editDraftUrl.trim(),
          description: editDraftDescription.trim() || null,
          method: editDraftMethod,
          priceMinSmallest: minSmallest,
          priceMaxSmallest: maxSmallest,
          priceAsset: editDraftPriceAsset,
        },
      }),
    );
    setIsSavingEndpoint(false);
    if (result.meta.requestStatus === 'fulfilled') {
      setEditingEndpointId(null);
      toast.success('Endpoint updated');
    } else {
      const payload = result.payload as
        | { kind: string; message: string }
        | undefined;
      if (payload?.kind === 'duplicate') {
        setEditDraftError(payload.message);
      } else if (payload) {
        toast.error(payload.message || 'Failed to update endpoint');
      }
    }
  };

  const confirmDeleteEndpoint = async () => {
    if (!agentUserId || !pendingDeleteEndpoint) return;
    setIsDeletingEndpoint(true);
    const result = await dispatch(
      deleteOwnedAgentEndpoint({
        agentUserId,
        endpointId: pendingDeleteEndpoint.id,
      }),
    );
    setIsDeletingEndpoint(false);
    if (result.meta.requestStatus === 'fulfilled') {
      toast.success('Endpoint removed');
      setPendingDeleteEndpoint(null);
    } else {
      const payload = result.payload as { message: string } | undefined;
      toast.error(payload?.message || 'Failed to delete endpoint');
      // Leave the modal open so the user can retry or cancel — the
      // toast surfaces the reason.
    }
  };

  // --- Wallet handlers ---
  // handleAddWallet moved to AddWalletModal.

  const handlePromoteWallet = async (walletId: string) => {
    if (!agentUserId) return;
    const result = await dispatch(
      setPrimaryOwnedAgentWallet({ agentUserId, walletId }),
    );
    if (result.meta.requestStatus === 'fulfilled') {
      toast.success('Primary wallet updated');
    } else {
      const payload = result.payload as { message: string } | undefined;
      toast.error(payload?.message || 'Failed to update primary');
    }
  };

  const confirmDeleteWallet = async () => {
    if (!agentUserId || !pendingDeleteWallet) return;
    setIsDeletingWallet(true);
    const result = await dispatch(
      deleteOwnedAgentWallet({
        agentUserId,
        walletId: pendingDeleteWallet.id,
      }),
    );
    setIsDeletingWallet(false);
    if (result.meta.requestStatus === 'fulfilled') {
      toast.success('Wallet removed');
      setPendingDeleteWallet(null);
    } else {
      const payload = result.payload as { message: string } | undefined;
      toast.error(payload?.message || 'Failed to delete wallet');
    }
  };

  if (loadStatus === 'loading' || loadStatus === 'idle') {
    return (
      <div className="my-agents">
        <div className="my-agents-container">
          <div className="my-agents-loading">
            <LoadingSpinner size="medium" message="Loading agent…" />
          </div>
        </div>
      </div>
    );
  }

  if (loadStatus === 'rejected' || !detail) {
    return (
      <div className="my-agents">
        <div className="my-agents-container">
          <div className="my-agents-error" role="alert">
            {loadError ?? 'Failed to load agent.'}
          </div>
          <div className="edit-agent__header-actions" style={{ marginTop: '1rem' }}>
            <Button variant="secondary" onClick={() => navigate('/agents/mine')}>
              Back to My Agents
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const saveError =
    saveRejection?.kind === 'display_name_taken'
      ? saveRejection.message
      : saveRejection?.kind === 'error'
      ? saveRejection.message
      : null;

  // endpointError derivation moved into AddEndpointModal along with
  // the composer that consumed it.

  return (
    <div className="my-agents edit-agent">
      <div className="my-agents-container">
        <header className="edit-agent__header">
          <div className="edit-agent__header-title">
            {/* Display name is the primary identifier for humans;
                @handle follows as the machine identifier. Falls back
                to "Untitled agent" if displayName is somehow empty
                (shouldn't happen — registration requires it — but
                a stale row from before the field was required could
                trip this). */}
            <h1>{detail.agent.displayName || 'Untitled agent'}</h1>
            <p>
              @{detail.agent.username}
              {' · '}
              <Link to={`/profile/${detail.agent.username}`} className="secondary-link">
                view public profile
              </Link>
            </p>
          </div>
          {/* Header carries only navigation now — each card owns its
              own save action inline (Profile has "Save profile" at
              the bottom of the card; Endpoints already has "Add
              endpoint" inside its composer). Keeps save context
              visually grouped with the fields it saves. */}
          <div className="edit-agent__header-actions">
            <Button variant="secondary" onClick={() => navigate('/agents/mine')}>
              Back to My Agents
            </Button>
          </div>
        </header>

        <section className="edit-agent__section">
          <h2>Profile</h2>
          <form
            onSubmit={handleSaveProfile}
            className="edit-agent__form"
            noValidate
          >
            <Input
              label="Display name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={100}
              required
              disabled={saveStatus === 'saving'}
              error={
                dnStatus === 'taken'
                  ? 'That display name is already taken.'
                  : saveRejection?.kind === 'display_name_taken'
                    ? saveRejection.message
                    : undefined
              }
            />
            {displayName.trim().length > 0 &&
              dnStatus !== 'idle' &&
              dnStatus !== 'taken' && (
                <p className={`dn-status dn-status--${dnStatus}`}>
                  {dnStatus === 'checking' && 'Checking availability…'}
                  {dnStatus === 'available' && 'Display name is available.'}
                </p>
              )}

            <label className="input-wrapper full-width">
              <span className="input-label">Bio</span>
              <textarea
                className="input-field edit-agent__textarea"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                maxLength={500}
                rows={3}
                disabled={saveStatus === 'saving'}
              />
            </label>

            <AgentCapabilityPicker
              value={capabilities}
              onChange={setCapabilities}
              disabled={saveStatus === 'saving'}
              label="Capabilities"
              hint="Pick any that apply. Empty = the agent won't show on capability-filtered discover."
            />

            <AgentChainPicker
              value={chains}
              onChange={setChains}
              disabled={saveStatus === 'saving'}
              label="Chains"
              hint="Which blockchains this agent operates on. Purely declarative for now — invocation still runs through Klyx receipts."
            />

            {saveError && (
              <div className="edit-agent__error" role="alert">
                {saveError}
              </div>
            )}

            <div className="edit-agent__actions">
              <Button
                type="submit"
                disabled={
                  saveStatus === 'saving' ||
                  !isProfileDirty ||
                  dnStatus === 'checking' ||
                  dnStatus === 'taken'
                }
              >
                {saveStatus === 'saving' ? 'Saving…' : 'Save profile'}
              </Button>
              {saveStatus === 'saved' && !isProfileDirty && (
                <span className="edit-agent__saved">Saved</span>
              )}
            </div>
          </form>
        </section>

        {/* Per-chain wallets. One row per (chain, address); at most
            one primary per chain (server enforces via partial-unique
            index). Owner can add, promote (only shows when the row
            isn't already primary), and delete. Deleting the primary
            leaves the chain with no primary until owner picks one —
            explicit is safer than silent promotion.

            Ordered ABOVE Endpoints intentionally — Wallets is short
            and reference-shaped (glance to confirm the primary
            address), while Endpoints grows long with the agent's
            surface. Putting Wallets first means the owner isn't
            scrolling past a long endpoints list to check payment
            destinations. */}
        <section className="edit-agent__section">
          <div className="edit-agent__section-header">
            <h2>Wallets</h2>
            <Button size="small" onClick={() => setAddWalletOpen(true)}>
              Add wallet
            </Button>
          </div>
          <p className="edit-agent__hint">
            Payment destinations per chain. One row is marked primary
            per chain — that's the address invokers pay by default.
            Add extras to rotate deposit addresses without losing
            history.
          </p>

          {detail.wallets.length === 0 && (
            <p className="edit-agent__empty">
              No wallets declared yet.
            </p>
          )}

          {detail.wallets.length > 0 && (
            <ul className="edit-agent__endpoints">
              {detail.wallets.map((w) => (
                <li key={w.id} className="edit-agent__endpoint">
                  <div className="edit-agent__endpoint-head">
                    <span
                      className={`edit-agent__method edit-agent__method--${w.chain}`}
                    >
                      {labelForChain(w.chain)}
                    </span>
                    {w.isPrimary && (
                      <span className="edit-agent__endpoint-label">
                        Primary
                      </span>
                    )}
                    {w.label && (
                      <span className="edit-agent__endpoint-label">
                        {w.label}
                      </span>
                    )}
                    <span
                      className="edit-agent__endpoint-url mono"
                      title={w.address}
                    >
                      {shortAddress(w.address)}
                    </span>
                    <div className="edit-agent__endpoint-actions">
                      {!w.isPrimary && (
                        <Button
                          variant="secondary"
                          size="small"
                          onClick={() => handlePromoteWallet(w.id)}
                          disabled={walletSaveStatus === 'saving'}
                        >
                          Make primary
                        </Button>
                      )}
                      <Button
                        variant="danger"
                        size="small"
                        onClick={() =>
                          setPendingDeleteWallet({
                            id: w.id,
                            chain: w.chain,
                            address: w.address,
                            label: w.label,
                          })
                        }
                        disabled={walletSaveStatus === 'saving'}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {/* Wallet composer moved to AddWalletModal — opened via
              the "Add wallet" button in the section header. */}
        </section>

        {/* Accepted payment lanes (issue #661). Per-agent (asset, chain)
            matrix that decides how endpoint.price_asset fans out at
            402 handshake time. Ordered between Wallets and Endpoints
            because it links the two — cells are "possible" per
            PAYMENT_ASSET_ALLOWED_CHAINS, "wallet-backed" per the
            agent's wallets, and "accepted" per the checkbox state. */}
        <section className="edit-agent__section">
          <div className="edit-agent__section-header">
            <h2>Accepted payment lanes</h2>
          </div>
          <p className="edit-agent__hint">
            Tick which tokens you&apos;ll accept on which chains. Callers
            paying for your endpoints will see these as their options —
            you get paid on whichever one they pick. A dashed cell means
            that token doesn&apos;t exist on that chain. A greyed cell
            means you haven&apos;t added a wallet on that chain yet —
            you can still tick it now and add the wallet later. A 🔧 cell
            means the platform isn&apos;t configured to route that lane
            yet — contact your Klyx admin.
          </p>

          {(() => {
            const walletChains = new Set(detail.wallets.map((w) => w.chain));
            const accepted = new Set(
              (detail.acceptedSettlements ?? []).map((l) => `${l.asset}|${l.chain}`),
            );
            // Servable set from the server — tuples the platform can
            // route right now. If a tuple is in PAYMENT_ASSET_ALLOWED_CHAINS
            // but not here, the platform is missing config (e.g. env
            // var unset). Rendered as a disabled cell with a 🔧 marker
            // + tooltip so the owner never clicks + hits the 400.
            const servable = new Set(
              (detail.platformServableLanes ?? []).map((l) => `${l.asset}|${l.chain}`),
            );
            const toggle = (asset: string, chain: string, currentlyChecked: boolean) => {
              // Belt-and-braces toast — even with servable-cell greying,
              // a race (env var changes mid-session, or a stale cache)
              // could still 400. Show the ops-focused message to the
              // owner instead of a silent devtools-only error.
              const onErr = (err: { message?: string } | undefined) => {
                toast.error(err?.message ?? 'Failed to save payment lane');
              };
              const payload = { agentUserId: detail.agent.userId, asset, chain };
              // Dispatch each branch separately so TS resolves each
              // thunk to its concrete AsyncThunkAction type (unioned
              // thunks trip the dispatch overload resolution).
              if (currentlyChecked) {
                void dispatch(deleteOwnedAgentAcceptedSettlement(payload))
                  .unwrap()
                  .catch(onErr);
              } else {
                void dispatch(addOwnedAgentAcceptedSettlement(payload))
                  .unwrap()
                  .catch(onErr);
              }
            };
            return (
              <div className="edit-agent__lanes">
                <table className="edit-agent__lanes-table">
                  <thead>
                    <tr>
                      <th />
                      {ALL_PAYMENT_CHAINS.map((c) => (
                        <th key={c}>{labelForChain(c)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {PAYMENT_ASSETS.map((asset) => {
                      const allowed = new Set(PAYMENT_ASSET_ALLOWED_CHAINS[asset] ?? []);
                      return (
                        <tr key={asset}>
                          <th scope="row">{asset}</th>
                          {ALL_PAYMENT_CHAINS.map((chain) => {
                            const possible = allowed.has(chain);
                            if (!possible) {
                              return (
                                <td
                                  key={chain}
                                  className="edit-agent__lane-cell edit-agent__lane-cell--impossible"
                                  aria-label={`${asset} on ${chain} — not possible`}
                                >
                                  —
                                </td>
                              );
                            }
                            const platformServable = servable.has(`${asset}|${chain}`);
                            const hasWallet = walletChains.has(chain);
                            const checked = accepted.has(`${asset}|${chain}`);

                            // Platform-side gap — cell is disabled entirely.
                            // Distinct 🔧 marker separates this ("Klyx admin
                            // needs to fix it") from the ⚠ dead-lane state
                            // ("you the owner need to add a wallet").
                            if (!platformServable) {
                              return (
                                <td
                                  key={chain}
                                  className="edit-agent__lane-cell edit-agent__lane-cell--platform-unavailable"
                                  aria-label={`${asset} on ${chain} — platform not configured yet`}
                                  title={`${asset} on ${chain.toUpperCase()} — the platform isn't configured to route this lane yet. Contact your Klyx admin to enable it.`}
                                >
                                  <span aria-hidden>🔧</span>
                                </td>
                              );
                            }

                            return (
                              <td
                                key={chain}
                                className={`edit-agent__lane-cell${
                                  hasWallet ? '' : ' edit-agent__lane-cell--no-wallet'
                                }`}
                              >
                                <label
                                  className="edit-agent__lane-checkbox"
                                  title={
                                    hasWallet
                                      ? `${asset} on ${chain.toUpperCase()}`
                                      : `${asset} on ${chain.toUpperCase()} — no wallet yet (lane reserves the intent; add the wallet in Wallets above)`
                                  }
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggle(asset, chain, checked)}
                                  />
                                  {!hasWallet && checked && (
                                    <span className="edit-agent__lane-warn" aria-hidden>
                                      ⚠
                                    </span>
                                  )}
                                </label>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </section>

        <section className="edit-agent__section">
          <div className="edit-agent__section-header">
            <h2>Endpoints</h2>
            <Button size="small" onClick={() => setAddEndpointOpen(true)}>
              Add endpoint
            </Button>
          </div>
          <p className="edit-agent__hint">
            Declare where + how invokers can reach this agent. Each entry
            is a callable URL with an HTTP method. Adding an endpoint
            here is the "how to interface" contract for anyone (human or
            agent) who wants to use this agent.
          </p>

          {detail.endpoints.length === 0 && (
            <div className="edit-agent__empty">
              No endpoints declared yet.
            </div>
          )}

          {detail.endpoints.length > 0 && (
            <ul className="edit-agent__endpoints">
              {detail.endpoints.map((ep) => {
                const isEditing = editingEndpointId === ep.id;
                const isAnotherRowEditing =
                  editingEndpointId !== null && !isEditing;
                return (
                  <li key={ep.id} className="edit-agent__endpoint">
                    {isEditing ? (
                      // ------- Edit mode: inline editor for this row -------
                      <div className="edit-agent__endpoint-editor">
                        <div className="edit-agent__composer-row">
                          <label className="input-wrapper edit-agent__method-picker">
                            <span className="input-label">Method</span>
                            <select
                              className="input-field"
                              value={editDraftMethod}
                              onChange={(e) =>
                                setEditDraftMethod(e.target.value as AgentEndpointMethod)
                              }
                              disabled={isSavingEndpoint}
                            >
                              {METHODS.map((m) => (
                                <option key={m} value={m}>{m}</option>
                              ))}
                            </select>
                          </label>
                          <Input
                            label="Label (optional)"
                            value={editDraftLabel}
                            onChange={(e) => setEditDraftLabel(e.target.value)}
                            maxLength={60}
                            disabled={isSavingEndpoint}
                          />
                        </div>
                        <Input
                          label="URL"
                          value={editDraftUrl}
                          onChange={(e) => setEditDraftUrl(e.target.value)}
                          type="url"
                          required
                          disabled={isSavingEndpoint}
                          error={editDraftError ?? undefined}
                        />
                        <label className="input-wrapper full-width">
                          <span className="input-label">Description (optional)</span>
                          <textarea
                            className="input-field edit-agent__textarea"
                            value={editDraftDescription}
                            onChange={(e) => setEditDraftDescription(e.target.value)}
                            maxLength={500}
                            rows={2}
                            disabled={isSavingEndpoint}
                          />
                        </label>
                        {/* Pricing — three fields in one row. Both amount
                            fields blank = Free. Min = Max = fixed price.
                            Different min + max = range. Server rejects a
                            non-Free price if the agent has no wallet on
                            the target chain. */}
                        <div className="edit-agent__price-row">
                          <Input
                            label="Min price (optional)"
                            value={editDraftPriceMin}
                            onChange={(e) => setEditDraftPriceMin(e.target.value)}
                            placeholder="0"
                            disabled={isSavingEndpoint}
                          />
                          <Input
                            label="Max price (optional)"
                            value={editDraftPriceMax}
                            onChange={(e) => setEditDraftPriceMax(e.target.value)}
                            placeholder="0"
                            disabled={isSavingEndpoint}
                          />
                          <label className="input-wrapper edit-agent__method-picker">
                            <span className="input-label">Asset</span>
                            <select
                              className="input-field"
                              value={editDraftPriceAsset}
                              onChange={(e) =>
                                setEditDraftPriceAsset(e.target.value)
                              }
                              disabled={isSavingEndpoint}
                            >
                              {PAYMENT_ASSETS.map((a) => (
                                <option key={a} value={a}>{a}</option>
                              ))}
                            </select>
                          </label>
                        </div>
                        <div className="edit-agent__actions">
                          <Button
                            onClick={handleSaveEditedEndpoint}
                            disabled={isSavingEndpoint || !editDraftUrl.trim()}
                          >
                            {isSavingEndpoint ? 'Saving…' : 'Save'}
                          </Button>
                          <Button
                            variant="secondary"
                            onClick={handleCancelEditEndpoint}
                            disabled={isSavingEndpoint}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      // ------- Read mode -------
                      <>
                        <div className="edit-agent__endpoint-head">
                          <span className={`edit-agent__method edit-agent__method--${ep.method.toLowerCase()}`}>
                            {ep.method}
                          </span>
                          {ep.label && (
                            <span className="edit-agent__endpoint-label">{ep.label}</span>
                          )}
                          <span className="edit-agent__endpoint-url mono">{ep.url}</span>
                          <span
                            className={`edit-agent__endpoint-price${
                              ep.priceMinSmallest === '0' && ep.priceMaxSmallest === '0'
                                ? ' edit-agent__endpoint-price--free'
                                : ''
                            }`}
                          >
                            {formatEndpointPrice({
                              priceMinSmallest: ep.priceMinSmallest,
                              priceMaxSmallest: ep.priceMaxSmallest,
                              priceAsset: ep.priceAsset,
                            })}
                          </span>
                          {(() => {
                            const v = endpointVerificationsByUrl[ep.url];
                            const status: 'none' | VerificationRow['status'] =
                              v?.status ?? 'none';
                            const label =
                              status === 'verified'
                                ? '✓ Verified'
                                : status === 'stale'
                                  ? '⚠ Stale'
                                  : status === 'pending'
                                    ? 'Pending'
                                    : 'Unverified';
                            const title =
                              status === 'verified' && v?.verifiedAt
                                ? `Verified ${new Date(v.verifiedAt).toLocaleString()}`
                                : status === 'stale'
                                  ? 'Verification is stale — passive probes stopped matching. Re-run verify to restore.'
                                  : status === 'pending'
                                    ? 'Verification challenge issued but not yet checked.'
                                    : 'No verification challenge on this endpoint yet.';
                            return (
                              <span
                                className={`edit-agent__endpoint-verify edit-agent__endpoint-verify--${status}`}
                                title={title}
                              >
                                {label}
                              </span>
                            );
                          })()}
                          <div className="edit-agent__endpoint-actions">
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => setVerifyEndpointId(ep.id)}
                              disabled={isAnotherRowEditing}
                            >
                              Verify
                            </Button>
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => handleStartEditEndpoint(ep)}
                              disabled={isAnotherRowEditing}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="danger"
                              size="small"
                              onClick={() =>
                                setPendingDeleteEndpoint({
                                  id: ep.id,
                                  method: ep.method,
                                  url: ep.url,
                                  label: ep.label ?? null,
                                })
                              }
                              disabled={isAnotherRowEditing}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                        {ep.description && (
                          <p className="edit-agent__endpoint-desc">{ep.description}</p>
                        )}
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {/* Endpoint composer moved to AddEndpointModal — opened
              via the "Add endpoint" button in the section header. */}
        </section>

        {/* ADR-012 verifications section — email + DNS live here
            (endpoint verify is on each endpoint card in the section
            above). Each subsection lists the existing verifications
            for that type + an "Add" button that opens the relevant
            modal. Domain-only email display + hostname list mirror
            what the public profile surfaces (#590). */}
        <section className="edit-agent__section">
          <div className="edit-agent__section-header">
            <h2>Verifications</h2>
          </div>
          <p className="edit-agent__hint">
            Optional trust signals for viewers of this agent. Verifying an
            email means Klyx can reach you at that address for stale-badge
            + dispute alerts. Verifying a DNS hostname proves you control
            the domain that hosts this agent.
          </p>

          {/* --- Email --- */}
          <div className="edit-agent__verify-block">
            <div className="edit-agent__verify-block-head">
              <h3>Email</h3>
              <Button
                size="small"
                variant="secondary"
                onClick={() => setVerifyEmailOpen(true)}
              >
                Add email
              </Button>
            </div>
            {emailVerifications.length === 0 && (
              <div className="edit-agent__empty">
                No email verification yet.
              </div>
            )}
            {emailVerifications.length > 0 && (
              <ul className="edit-agent__verify-list">
                {emailVerifications.map((v) => (
                  <li key={v.id} className="edit-agent__verify-row">
                    <span className="edit-agent__verify-target">
                      {v.targetValue}
                    </span>
                    <span
                      className={`edit-agent__endpoint-verify edit-agent__endpoint-verify--${v.status}`}
                      title={
                        v.status === 'verified' && v.verifiedAt
                          ? `Verified ${new Date(v.verifiedAt).toLocaleString()}`
                          : v.status === 'pending'
                            ? 'Confirmation link sent; recipient hasn’t clicked it yet.'
                            : v.status === 'stale'
                              ? 'Verification is stale.'
                              : ''
                      }
                    >
                      {v.status === 'verified'
                        ? '✓ Verified'
                        : v.status === 'pending'
                          ? 'Pending'
                          : v.status === 'stale'
                            ? '⚠ Stale'
                            : v.status}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* --- DNS --- */}
          <div className="edit-agent__verify-block">
            <div className="edit-agent__verify-block-head">
              <h3>DNS</h3>
              <Button
                size="small"
                variant="secondary"
                onClick={() => setVerifyDnsOpen(true)}
              >
                Add hostname
              </Button>
            </div>
            {dnsVerifications.length === 0 && (
              <div className="edit-agent__empty">
                No DNS hostname verification yet.
              </div>
            )}
            {dnsVerifications.length > 0 && (
              <ul className="edit-agent__verify-list">
                {dnsVerifications.map((v) => (
                  <li key={v.id} className="edit-agent__verify-row">
                    <span className="edit-agent__verify-target">
                      {v.targetValue}
                    </span>
                    <span
                      className={`edit-agent__endpoint-verify edit-agent__endpoint-verify--${v.status}`}
                      title={
                        v.status === 'verified' && v.verifiedAt
                          ? `Verified ${new Date(v.verifiedAt).toLocaleString()}`
                          : v.status === 'pending'
                            ? 'Challenge issued; TXT record not yet checked.'
                            : v.status === 'stale'
                              ? 'Verification is stale.'
                              : ''
                      }
                    >
                      {v.status === 'verified'
                        ? '✓ Verified'
                        : v.status === 'pending'
                          ? 'Pending'
                          : v.status === 'stale'
                            ? '⚠ Stale'
                            : v.status}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

      </div>

      {agentUserId && (
        <AddEndpointModal
          isOpen={addEndpointOpen}
          agentUserId={agentUserId}
          onClose={() => setAddEndpointOpen(false)}
        />
      )}
      {agentUserId && (
        <AddWalletModal
          isOpen={addWalletOpen}
          agentUserId={agentUserId}
          onClose={() => setAddWalletOpen(false)}
        />
      )}

      {agentUserId && verifyEndpointId && (() => {
        const ep = detail?.endpoints.find((e) => e.id === verifyEndpointId);
        if (!ep) return null;
        return (
          <VerifyEndpointModal
            isOpen={true}
            agentUserId={agentUserId}
            endpointId={ep.id}
            endpointUrl={ep.url}
            onClose={(updated) => {
              // Refetch on success so the status chip picks up the
              // new row without a page reload; skip when the modal
              // was just dismissed without a check.
              if (updated) refetchVerifications();
              setVerifyEndpointId(null);
            }}
          />
        );
      })()}

      {agentUserId && (
        <VerifyEmailModal
          isOpen={verifyEmailOpen}
          agentUserId={agentUserId}
          onClose={() => {
            setVerifyEmailOpen(false);
            // Email confirm happens out-of-band (recipient clicks
            // the link), so refetch to catch it if the owner left
            // the modal open long enough for that to happen.
            refetchVerifications();
          }}
        />
      )}

      {agentUserId && (
        <VerifyDnsModal
          isOpen={verifyDnsOpen}
          agentUserId={agentUserId}
          onClose={(updated) => {
            setVerifyDnsOpen(false);
            if (updated) refetchVerifications();
          }}
        />
      )}

      <ConfirmDialog
        isOpen={pendingDeleteEndpoint !== null}
        title="Delete endpoint?"
        message={
          pendingDeleteEndpoint
            ? `${pendingDeleteEndpoint.method} ${pendingDeleteEndpoint.url}${
                pendingDeleteEndpoint.label
                  ? ` (${pendingDeleteEndpoint.label})`
                  : ''
              } — this only removes the declaration on Klyx. Any receipts already emitted against this endpoint are unaffected.`
            : ''
        }
        confirmText="Delete endpoint"
        destructive
        isProcessing={isDeletingEndpoint}
        onConfirm={confirmDeleteEndpoint}
        onCancel={() => {
          if (!isDeletingEndpoint) setPendingDeleteEndpoint(null);
        }}
      />

      <ConfirmDialog
        isOpen={pendingDeleteWallet !== null}
        title="Delete wallet?"
        message={
          pendingDeleteWallet
            ? `${labelForChain(pendingDeleteWallet.chain)} ${shortAddress(pendingDeleteWallet.address)}${
                pendingDeleteWallet.label
                  ? ` (${pendingDeleteWallet.label})`
                  : ''
              } — this only removes the declaration on Klyx. Any funds held at the address are unaffected. If this was the primary for its chain, no wallet will be primary until you pick one.`
            : ''
        }
        confirmText="Delete wallet"
        destructive
        isProcessing={isDeletingWallet}
        onConfirm={confirmDeleteWallet}
        onCancel={() => {
          if (!isDeletingWallet) setPendingDeleteWallet(null);
        }}
      />
    </div>
  );
};

export default EditAgent;
